import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getEntityHistory } from '../../api';
import { formatDateTime } from '../../utils';
import styles from './HistoryTab.module.css';

function toTitleCase(str) {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(val) {
  if (val === null || val === undefined) return '—';
  if (val === true || val === 'true') return 'Yes';
  if (val === false || val === 'false') return 'No';
  const str = String(val);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) return formatDateTime(str);
  return str;
}

function ActionBadge({ action }) {
  const cls = {
    Created: styles.badgeCreated,
    Updated: styles.badgeUpdated,
    Archived: styles.badgeArchived,
    Unarchived: styles.badgeUnarchived,
  }[action] || styles.badgeUpdated;
  return <span className={`${styles.badge} ${cls}`}>{action}</span>;
}

function ChangesetTable({ action, changeset }) {
  const entries = Object.entries(changeset || {});
  if (entries.length === 0) return <p className={styles.noChanges}>No field details recorded.</p>;

  if (action === 'Created') {
    return (
      <table className={styles.changeTable}>
        <thead>
          <tr>
            <th className={styles.changeThField}>Field</th>
            <th className={styles.changeThValue}>Value</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([field, value]) => (
            <tr key={field}>
              <td className={styles.changeTdField}>{toTitleCase(field)}</td>
              <td className={styles.changeTdNew}>{formatValue(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <table className={styles.changeTable}>
      <thead>
        <tr>
          <th className={styles.changeThField}>Field</th>
          <th className={styles.changeThOld}>Before</th>
          <th className={styles.changeThNew}>After</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([field, pair]) => {
          const [oldVal, newVal] = Array.isArray(pair) ? pair : [undefined, pair];
          return (
            <tr key={field}>
              <td className={styles.changeTdField}>{toTitleCase(field)}</td>
              <td className={styles.changeTdOld}>{formatValue(oldVal)}</td>
              <td className={styles.changeTdNew}>{formatValue(newVal)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ChangesetSummary({ action, changeset }) {
  if (action === 'Created') return 'Record created';
  const fields = Object.keys(changeset || {});
  if (fields.length === 0) return null;
  if (action === 'Archived' || action === 'Unarchived') {
    const pair = changeset.is_archived;
    if (Array.isArray(pair)) {
      return `is_archived: ${formatValue(pair[0])} → ${formatValue(pair[1])}`;
    }
  }
  return fields.map(toTitleCase).join(', ');
}

function HistoryItem({ item }) {
  const [expanded, setExpanded] = useState(false);
  const hasChangeset = item.changeset && Object.keys(item.changeset).length > 0;

  return (
    <div className={styles.item}>
      <div className={styles.itemGutter}>
        <div className={styles.dot} />
        <div className={styles.line} />
      </div>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardMeta}>
            <span className={styles.version}>v{item.version}</span>
            <ActionBadge action={item.action} />
            <span className={styles.timestamp}>{formatDateTime(item.timestamp)}</span>
            <span className={styles.user}>{item.user || 'System'}</span>
          </div>
          {hasChangeset && (
            <button
              type="button"
              className={styles.expandBtn}
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded ? <ChevronUp size={14} strokeWidth={1.5} /> : <ChevronDown size={14} strokeWidth={1.5} />}
              {expanded ? 'Hide changes' : 'Show changes'}
            </button>
          )}
        </div>
        <p className={styles.summary}>
          <ChangesetSummary action={item.action} changeset={item.changeset} />
        </p>
        {expanded && hasChangeset && (
          <div className={styles.changesetWrap}>
            <ChangesetTable action={item.action} changeset={item.changeset} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function HistoryTab({ entityPath }) {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setItems([]);
    setNextCursor(null);
    setTotal(0);
    setError('');
    setIsLoading(true);

    getEntityHistory(entityPath)
      .then((data) => {
        if (cancelled) return;
        setItems(data.items || []);
        setNextCursor(data.next_cursor || null);
        setTotal(data.total ?? 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load history.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [entityPath]);

  const handleLoadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const data = await getEntityHistory(entityPath, { cursor: nextCursor });
      setItems((prev) => [...prev, ...(data.items || [])]);
      setNextCursor(data.next_cursor || null);
    } catch (err) {
      setError(err.message || 'Failed to load more history.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [entityPath, nextCursor, isLoadingMore]);

  if (isLoading) {
    return <p className={styles.loadingState} role="status">Loading history…</p>;
  }

  if (error) {
    return <p className={styles.errorState} role="alert">{error}</p>;
  }

  if (total === 0) {
    return <p className={styles.emptyState}>No history found.</p>;
  }

  return (
    <div className={styles.root}>
      <p className={styles.totalCount}>{total} {total === 1 ? 'change' : 'changes'}</p>
      <div className={styles.timeline}>
        {items.map((item, idx) => (
          <HistoryItem key={`${item.version}-${idx}`} item={item} />
        ))}
      </div>
      {nextCursor && (
        <div className={styles.loadMoreRow}>
          <button
            type="button"
            className={styles.loadMoreBtn}
            onClick={handleLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
